import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import axios from 'axios';
import { Car, DollarSign, Calendar, MapPin, Check } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const VehicleRequest = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  
  const [formData, setFormData] = useState({
    make: '',
    model: '',
    year_min: 2015,
    year_max: new Date().getFullYear(),
    budget: '',
    location: '',
    additional_notes: ''
  });
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!user) {
      navigate('/login');
      return;
    }

    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      await axios.post(
        `${API}/vehicle-requests`,
        { 
          ...formData, 
          budget: parseFloat(formData.budget),
          year_min: parseInt(formData.year_min),
          year_max: parseInt(formData.year_max)
        },
        {
          headers: { Authorization: `Bearer ${token}` },
          withCredentials: true
        }
      );
      setSuccess(true);
      setTimeout(() => {
        navigate('/dashboard');
      }, 2500);
    } catch (error) {
      console.error('Error submitting request:', error);
      alert('Failed to submit request. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  return (
    <div className="min-h-screen pt-20 pb-12">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-12">
          <div className="w-16 h-16 bg-primary rounded-full flex items-center justify-center mx-auto mb-4">
            <Car className="w-8 h-8" />
          </div>
          <h1 className="text-4xl font-heading font-bold mb-4">Request a Vehicle</h1>
          <p className="text-textSecondary">
            Can't find what you're looking for? Let us source your dream car from US auctions.
          </p>
        </div>

        {success ? (
          <div className="glass p-12 rounded-sm text-center">
            <div className="w-20 h-20 bg-green-600 rounded-full flex items-center justify-center mx-auto mb-6">
              <Check className="w-10 h-10" />
            </div>
            <h2 className="text-3xl font-heading font-bold mb-4 text-green-500">Request Submitted!</h2>
            <p className="text-textSecondary mb-2">We'll search US auctions and get back to you within 24-48 hours.</p>
            <p className="text-textSecondary">Redirecting to dashboard...</p>
          </div>
        ) : (
          <form onSubmit={handleSubmit} className="glass p-8 rounded-sm space-y-6">
            {/* Make & Model */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <label className="block text-sm font-medium mb-2">Make *</label>
                <div className="relative">
                  <Car className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-textSecondary" />
                  <input
                    type="text"
                    name="make"
                    value={formData.make}
                    onChange={handleChange}
                    required
                    placeholder="e.g., Toyota"
                    className="w-full bg-surfaceHighlight border border-white/10 text-white rounded-sm pl-12 pr-4 py-3 focus:border-primary focus:ring-1 focus:ring-primary"
                  />
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium mb-2">Model *</label>
                <input
                  type="text"
                  name="model"
                  value={formData.model}
                  onChange={handleChange}
                  required
                  placeholder="e.g., Camry"
                  className="w-full bg-surfaceHighlight border border-white/10 text-white rounded-sm px-4 py-3 focus:border-primary focus:ring-1 focus:ring-primary"
                />
              </div>
            </div>

            {/* Year Range */}
            <div>
              <label className="block text-sm font-medium mb-2">Year Range *</label>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-xs text-textSecondary mb-1">From</label>
                  <div className="relative">
                    <Calendar className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-textSecondary" />
                    <input
                      type="number"
                      name="year_min"
                      value={formData.year_min}
                      onChange={handleChange}
                      required
                      min="1990"
                      max={new Date().getFullYear()}
                      className="w-full bg-surfaceHighlight border border-white/10 text-white rounded-sm pl-12 pr-4 py-3 focus:border-primary focus:ring-1 focus:ring-primary"
                    />
                  </div>
                </div>
                <div>
                  <label className="block text-xs text-textSecondary mb-1">To</label>
                  <input
                    type="number"
                    name="year_max"
                    value={formData.year_max}
                    onChange={handleChange}
                    required
                    min={formData.year_min}
                    max={new Date().getFullYear()}
                    className="w-full bg-surfaceHighlight border border-white/10 text-white rounded-sm px-4 py-3 focus:border-primary focus:ring-1 focus:ring-primary"
                  />
                </div>
              </div>
            </div>

            {/* Budget */}
            <div>
              <label className="block text-sm font-medium mb-2">Budget (USD) *</label>
              <div className="relative">
                <DollarSign className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-textSecondary" />
                <input
                  type="number"
                  name="budget"
                  value={formData.budget}
                  onChange={handleChange}
                  required
                  min="1000"
                  step="100"
                  placeholder="20000"
                  className="w-full bg-surfaceHighlight border border-white/10 text-white rounded-sm pl-12 pr-4 py-3 focus:border-primary focus:ring-1 focus:ring-primary"
                />
              </div>
              <p className="text-xs text-textSecondary mt-1">Your maximum budget for the vehicle</p>
            </div>

            {/* Location */}
            <div>
              <label className="block text-sm font-medium mb-2">Location *</label>
              <div className="relative">
                <MapPin className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-textSecondary" />
                <input
                  type="text"
                  name="location"
                  value={formData.location}
                  onChange={handleChange}
                  required
                  placeholder="e.g., Lagos, Nigeria"
                  className="w-full bg-surfaceHighlight border border-white/10 text-white rounded-sm pl-12 pr-4 py-3 focus:border-primary focus:ring-1 focus:ring-primary"
                />
              </div>
              <p className="text-xs text-textSecondary mt-1">Where should the vehicle be delivered?</p>
            </div>

            {/* Additional Notes */}
            <div>
              <label className="block text-sm font-medium mb-2">Additional Notes</label>
              <textarea
                name="additional_notes"
                value={formData.additional_notes}
                onChange={handleChange}
                rows="4"
                placeholder="Any specific requirements, preferences, or details..."
                className="w-full bg-surfaceHighlight border border-white/10 text-white rounded-sm px-4 py-3 focus:border-primary focus:ring-1 focus:ring-primary resize-none"
              />
            </div>

            <button
              type="submit"
              disabled={loading}
              className="w-full bg-primary hover:bg-primaryHover text-white rounded-sm font-bold px-6 py-4 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {loading ? 'Submitting Request...' : 'Submit Request'}
            </button>

            <p className="text-xs text-textSecondary text-center">
              We'll review your request and reach out within 24-48 hours
            </p>
          </form>
        )}
      </div>
    </div>
  );
};

export default VehicleRequest;
